import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { get } from "../modules/server";
import { logAndNotifyError, logDebugSilently } from "../modules/logger";
import { getUserInfo, geValidUntil } from "../modules/server";

import { UserInfo } from "../types/user-info";

import PubSub from "pubsub-js";
import { IGlobalData } from "../types/globalData";
import { DBModuleType, ModuleTypeId } from "../types/DBTables";
import { DTOBuilding } from "../types/building";
import { SystemText } from "../types/help-text";
import { IBeskrivningarBFL } from "../types/module-type";

interface GlobalDataContextState {
  loading: boolean;
  error?: Error;
  data?: IGlobalData;
  user?: UserInfo;
  buildings?: DTOBuilding[];
  moduleTypes: DBModuleType[];
  isDebug: boolean;
  isDebugBIM: boolean;
  isOnline: boolean;
  beräkningsflikarText: IBeskrivningarBFL;
}

let cachedUserInfo = getUserInfo();
if (cachedUserInfo) {
  const validUntil = geValidUntil();
  const acceptIfValid = new Date();
  acceptIfValid.setHours(acceptIfValid.getHours() - 1); //At least one hour validity, if not - force login
  if (validUntil <= acceptIfValid) cachedUserInfo = undefined;
}

const initalState: GlobalDataContextState = {
  data: undefined,
  loading: true,
  error: undefined,
  user: cachedUserInfo,
  buildings: undefined,
  moduleTypes: [],
  isDebug: false,
  isDebugBIM: false,
  isOnline: true,
  beräkningsflikarText: {} as IBeskrivningarBFL,
};

const GlobalDataContext = createContext(initalState);
logDebugSilently("GlobalDataContext.created", initalState);

let isFetchingGlobalData = false;
let isFetchingBuildings = false;
let isFetchingBFInfo = false;

export const GlobalDataContextProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<GlobalDataContextState>(() => {
    logDebugSilently("GlobalDataContextProvider.initState", initalState);

    return initalState;
  });

  const fetchGlobalData = useCallback(async () => {
    try {
      if (isFetchingGlobalData) return;
      logDebugSilently("fetchGlobalData:" + isFetchingGlobalData);
      isFetchingGlobalData = true;
      const data = await get("GlobalData");
      isFetchingGlobalData = false;
      if (data?.success) {
        //No errors
        const globalData: IGlobalData = JSON.parse(data.jsonData);
        logDebugSilently("fetchGlobalData.currentState", state);
        setState({ ...state, ...{ data: globalData, moduleTypes: data.moduleTypes, loading: false } });

        //todo: Remove - Test
      }
    } catch (err) {
      logAndNotifyError(err);
    }
  }, [state]);

  const fetchBuildings = useCallback(
    async (clr: string) => {
      try {
        if (isFetchingBuildings) return;
        logDebugSilently("fetchBuildings(" + clr + ").currentState", state);
        isFetchingBuildings = true;
        const config = await get("My/Configuration");
        isFetchingBuildings = false;
        if (config.buildings) {
          setState({ ...state, ...{ buildings: config.buildings } });
          if (config.message) {
            logAndNotifyError(config.message);
          }
        }
      } catch (err) {
        logAndNotifyError(err);
      }
    },
    [state]
  );

  const fetchBeräkningsflikarInfo = useCallback(async () => {
    try {
      if (isFetchingBFInfo) return;
      logDebugSilently("fetchBeräkningsflikarInfo");
      isFetchingBFInfo = true;
      const resp = await get("GetSystemTextGroup/Beräkningsflikar");
      isFetchingBFInfo = false;
      if (resp.success) {
        var obj = resp.texts.reduce((acc: any, cur: SystemText) => {
          acc[cur.id] = cur;
          return acc;
        }, {} as any);
        setState({ ...state, ...{ beräkningsflikarText: obj } });
      }
    } catch (err) {
      logAndNotifyError(err);
    }
  }, [state]);

  useEffect(() => {
    PubSub.subscribe("LoggedIn", (msg, userInfo: UserInfo) => {
      const newData = { ...state, user: userInfo, isOnline: true };
      setState(newData);
    });

    PubSub.subscribe("NewDataFile", (msg) => {
      fetchGlobalData();
    });

    PubSub.subscribe("RefreshBuildings", (msg) => {
      fetchBuildings("pubsub");
    });

    PubSub.subscribe("RefreshBFLInfo", (msg) => {
      fetchBeräkningsflikarInfo();
    });

    PubSub.subscribe("ToggleDebug", (msg) => {
      const newData = { ...state, isDebug: !state.isDebug };
      setState(newData);
    });

    PubSub.subscribe("ToggleDebugBIM", (msg) => {
      const newData = { ...state, isDebugBIM: !state.isDebugBIM };
      setState(newData);
    });

    PubSub.subscribe("Online", (msg, data) => {
      const newData = { ...state, isOnline: data.isOnline };
      setState(newData);
    });

    PubSub.subscribe("LoggedOut", (msg) => {
      setState({
        data: undefined,
        loading: true,
        error: undefined,
        user: undefined,
        buildings: undefined,
        moduleTypes: [],
        isDebug: false,
        isDebugBIM: false,
        isOnline: false,
        beräkningsflikarText: {} as IBeskrivningarBFL,
      });
    });

    return () => {
      PubSub.unsubscribe("LoggedIn");
      PubSub.unsubscribe("NewDataFile");
      PubSub.unsubscribe("RefreshBuildings");
      PubSub.unsubscribe("RefreshBFLInfo");
      PubSub.unsubscribe("ToggleDebug");
      PubSub.unsubscribe("ToggleDebugBIM");
      PubSub.unsubscribe("Online");
    };
  }, [state, fetchGlobalData, fetchBuildings, fetchBeräkningsflikarInfo]);

  if (state.user && !state.data) fetchGlobalData();

  if (state.user && !state.buildings) fetchBuildings("render");

  if (state.user && !state.beräkningsflikarText.beskr_BFK_J1) fetchBeräkningsflikarInfo();

  return <GlobalDataContext.Provider value={state}>{children}</GlobalDataContext.Provider>;
};

export const useGlobalData = () => useContext(GlobalDataContext);
